import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Vaccines as VaccinesIcon,
  FamilyRestroom as FamilyRestroomIcon,
  SocialDistance as SocialDistanceIcon,
  Build as BuildIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AllergiesTab from './AllergiesTab';
import ImmunizationsSection from './ImmunizationsSection';
import MedicalHistoryService from '../../services/medicalHistoryService';
import { DatePicker } from '@mui/x-date-pickers';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiCardHeader-root': {
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const HistorySection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const HistoryItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`medical-history-tabpanel-${index}`}
    aria-labelledby={`medical-history-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const MedicalHistoryTab = ({ patient, editMode = false }) => {
  const [tabValue, setTabValue] = useState(0);
  const [expandedSections, setExpandedSections] = useState(new Set(['allergies', 'immunizations']));
  const [problemDialogOpen, setProblemDialogOpen] = useState(false);
  const [familyHistoryDialogOpen, setFamilyHistoryDialogOpen] = useState(false);
  const [socialHistoryDialogOpen, setSocialHistoryDialogOpen] = useState(false);
  const [surgicalHistoryDialogOpen, setSurgicalHistoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // State for medical history data
  const [problemList, setProblemList] = useState([]);
  const [familyHistory, setFamilyHistory] = useState([]);
  const [socialHistory, setSocialHistory] = useState({});
  const [surgicalHistory, setSurgicalHistory] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state for dialogs
  const [problemForm, setProblemForm] = useState({
    condition: '',
    status: 'active',
    severity: 'mild',
    onsetDate: '',
    notes: ''
  });

  const [familyHistoryForm, setFamilyHistoryForm] = useState({
    relationship: '',
    condition: '',
    age: '',
    status: 'alive',
    notes: ''
  });

  const [socialHistoryForm, setSocialHistoryForm] = useState({
    smoking: { status: 'unknown', details: '' },
    alcohol: { status: 'unknown', details: '' },
    occupation: '',
    livingSituation: '',
    exercise: '',
    diet: ''
  });

  const [surgicalHistoryForm, setSurgicalHistoryForm] = useState({
    procedure: '',
    date: '',
    facility: '',
    surgeon: '',
    notes: ''
  });

  // Load medical history data when component mounts or patient changes
  const loadMedicalHistory = useCallback(async () => {
    if (!patient?.id || !patient?.facilityId) return;
    
    setLoading(true);
    try {
      const historyData = await MedicalHistoryService.getComprehensiveMedicalHistory(patient.id, patient.facilityId);
      setProblemList(historyData.problems || []);
      setFamilyHistory(historyData.familyHistory || []);
      setSocialHistory(historyData.socialHistory || {});
      setSurgicalHistory(historyData.surgicalHistory || []);
    } catch (error) {
      console.error('Error loading medical history:', error);
      setError('Failed to load medical history data. Please try again later.');
      // Keep empty state on error
      setProblemList([]);
      setFamilyHistory([]);
      setSocialHistory({});
      setSurgicalHistory([]);
    } finally {
      setLoading(false);
    }
  }, [patient?.id, patient?.facilityId]);

  useEffect(() => {
    if (patient?.id) {
      loadMedicalHistory();
    }
  }, [patient?.id, loadMedicalHistory]);

  // Form handlers
  const handleProblemFormChange = (field, value) => {
    setProblemForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFamilyHistoryFormChange = (field, value) => {
    setFamilyHistoryForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSocialHistoryFormChange = (field, value) => {
    if (field === 'smoking' || field === 'alcohol') {
      setSocialHistoryForm(prev => ({
        ...prev,
        [field]: { ...prev[field], details: value }
      }));
    } else {
      setSocialHistoryForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSurgicalHistoryFormChange = (field, value) => {
    setSurgicalHistoryForm(prev => ({ ...prev, [field]: value }));
  };

  // Form submission handlers
  const handleAddProblem = async () => {
    if (!patient?.id || !patient?.facilityId) return;
    
    // Basic validation
    if (!problemForm.condition.trim() || !problemForm.onsetDate) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'warning' });
      return;
    }
    
    setSubmitting(true);
    try {
      const problemData = {
        ...problemForm,
        patientId: patient.id,
        facilityId: patient.facilityId
      };
      
      const newProblem = await MedicalHistoryService.addProblem(patient.id, patient.facilityId, problemData);
      setProblemList(prev => [...prev, newProblem]);
      setProblemDialogOpen(false);
      resetProblemForm();
      setSnackbar({ open: true, message: 'Problem added successfully', severity: 'success' });
    } catch (error) {
      console.error('Error adding problem:', error);
      setSnackbar({ open: true, message: 'Failed to add problem. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddFamilyHistory = async () => {
    if (!patient?.id || !patient?.facilityId) return;
    
    // Basic validation
    if (!familyHistoryForm.relationship.trim() || !familyHistoryForm.condition.trim() || !familyHistoryForm.age) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'warning' });
      return;
    }
    
    setSubmitting(true);
    try {
      const familyData = {
        ...familyHistoryForm,
        patientId: patient.id,
        facilityId: patient.facilityId
      };
      
      const newFamilyItem = await MedicalHistoryService.addFamilyHistory(patient.id, patient.facilityId, familyData);
      setFamilyHistory(prev => [...prev, newFamilyItem]);
      setFamilyHistoryDialogOpen(false);
      resetFamilyHistoryForm();
      setSnackbar({ open: true, message: 'Family history added successfully', severity: 'success' });
    } catch (error) {
      console.error('Error adding family history:', error);
      setSnackbar({ open: true, message: 'Failed to add family history. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSocialHistory = async () => {
    if (!patient?.id || !patient?.facilityId) return;
    
    setSubmitting(true);
    try {
      const socialData = {
        ...socialHistoryForm,
        patientId: patient.id,
        facilityId: patient.facilityId
      };
      
      const updatedSocialHistory = await MedicalHistoryService.updateSocialHistory(patient.id, patient.facilityId, socialData);
      setSocialHistory(updatedSocialHistory);
      setSocialHistoryDialogOpen(false);
      resetSocialHistoryForm();
      setSnackbar({ open: true, message: 'Social history updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Error updating social history:', error);
      setSnackbar({ open: true, message: 'Failed to update social history. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSurgicalHistory = async () => {
    if (!patient?.id || !patient?.facilityId) return;
    
    // Basic validation
    if (!surgicalHistoryForm.procedure.trim() || !surgicalHistoryForm.date || !surgicalHistoryForm.facility.trim()) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'warning' });
      return;
    }
    
    setSubmitting(true);
    try {
      const surgicalData = {
        ...surgicalHistoryForm,
        patientId: patient.id,
        facilityId: patient.facilityId
      };
      
      const newSurgery = await MedicalHistoryService.addSurgicalHistory(patient.id, patient.facilityId, surgicalData);
      setSurgicalHistory(prev => [...prev, newSurgery]);
      setSurgicalHistoryDialogOpen(false);
      resetSurgicalHistoryForm();
      setSnackbar({ open: true, message: 'Surgical history added successfully', severity: 'success' });
    } catch (error) {
      console.error('Error adding surgical history:', error);
      setSnackbar({ open: true, message: 'Failed to add surgical history. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Form reset functions
  const resetProblemForm = () => {
    setProblemForm({
      condition: '',
      status: 'active',
      severity: 'mild',
      onsetDate: '',
      notes: ''
    });
  };

  const resetFamilyHistoryForm = () => {
    setFamilyHistoryForm({
      relationship: '',
      condition: '',
      age: '',
      status: 'alive',
      notes: ''
    });
  };

  const resetSocialHistoryForm = () => {
    setSocialHistoryForm({
      smoking: { status: 'unknown', details: '' },
      alcohol: { status: 'unknown', details: '' },
      occupation: '',
      livingSituation: '',
      exercise: '',
      diet: ''
    });
  };

  const resetSurgicalHistoryForm = () => {
    setSurgicalHistoryForm({
      procedure: '',
      date: '',
      facility: '',
      surgeon: '',
      notes: ''
    });
  };

  // Dialog open handlers with form initialization
  const handleOpenProblemDialog = () => {
    resetProblemForm();
    setProblemDialogOpen(true);
  };

  const handleOpenFamilyHistoryDialog = () => {
    resetFamilyHistoryForm();
    setFamilyHistoryDialogOpen(true);
  };

  const handleOpenSocialHistoryDialog = () => {
    setSocialHistoryForm({
      smoking: socialHistory.smoking || { status: 'unknown', details: '' },
      alcohol: socialHistory.alcohol || { status: 'unknown', details: '' },
      occupation: socialHistory.occupation || '',
      livingSituation: socialHistory.livingSituation || '',
      exercise: socialHistory.exercise || '',
      diet: socialHistory.diet || ''
    });
    setSocialHistoryDialogOpen(true);
  };

  const handleOpenSurgicalHistoryDialog = () => {
    resetSurgicalHistoryForm();
    setSurgicalHistoryDialogOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleSection = (section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'error';
      case 'resolved': return 'success';
      case 'chronic': return 'warning';
      case 'deceased': return 'default';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'severe': return 'error';
      case 'moderate': return 'warning';
      case 'mild': return 'success';
      default: return 'default';
    }
  };

  const renderProblemList = () => (
    <HistorySection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h3">
          Problem List
        </Typography>
        {editMode && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={handleOpenProblemDialog}
          >
            Add Problem
          </Button>
        )}
      </Box>
      
      {problemList.length === 0 ? (
        <Alert severity="info">No problems documented</Alert>
      ) : (
        problemList.map((problem) => (
          <HistoryItem key={problem.id}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {problem.condition}
                  </Typography>
                  <Chip 
                    label={problem.status} 
                    size="small" 
                    color={getStatusColor(problem.status)}
                  />
                  <Chip 
                    label={problem.severity} 
                    size="small" 
                    color={getSeverityColor(problem.severity)}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Onset: {new Date(problem.onsetDate).toLocaleDateString()}
                </Typography>
                {problem.notes && (
                  <Typography variant="body2">{problem.notes}</Typography>
                )}
              </Box>
              {editMode && (
                <Box>
                  <IconButton size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </HistoryItem>
        ))
      )}
    </HistorySection>
  );

  const renderFamilyHistory = () => (
    <HistorySection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h3">
          Family History
        </Typography>
        {editMode && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={handleOpenFamilyHistoryDialog}
          >
            Add Family Member
          </Button>
        )}
      </Box>
      
      {familyHistory.length === 0 ? (
        <Alert severity="info">No family history documented</Alert>
      ) : (
        familyHistory.map((item) => (
          <HistoryItem key={item.id}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.relationship}
                  </Typography>
                  <Chip 
                    label={item.status} 
                    size="small" 
                    color={getStatusColor(item.status)}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {item.condition} - Age {item.age}
                </Typography>
                {item.notes && (
                  <Typography variant="body2">{item.notes}</Typography>
                )}
              </Box>
              {editMode && (
                <Box>
                  <IconButton size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </HistoryItem>
        ))
      )}
    </HistorySection>
  );

  const renderSocialHistory = () => (
    <HistorySection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h3">
          Social History
        </Typography>
        {editMode && (
          <Button
            startIcon={<EditIcon />}
            variant="outlined"
            size="small"
            onClick={handleOpenSocialHistoryDialog}
          >
            Edit
          </Button>
        )}
      </Box>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader title="Lifestyle" />
            <CardContent>
              <List dense>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText 
                    primary="Smoking" 
                    secondary={socialHistory.smoking?.details || 'No smoking history documented'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="info" /></ListItemIcon>
                  <ListItemText 
                    primary="Alcohol" 
                    secondary={socialHistory.alcohol?.details || 'No alcohol history documented'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><HistoryIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Exercise" 
                    secondary={socialHistory.exercise || 'No exercise history documented'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </StyledCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader title="Personal" />
            <CardContent>
              <List dense>
                <ListItem>
                  <ListItemIcon><HistoryIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Occupation" 
                    secondary={socialHistory.occupation || 'No occupation documented'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><HistoryIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Living Situation" 
                    secondary={socialHistory.livingSituation || 'No living situation documented'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><HistoryIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Diet" 
                    secondary={socialHistory.diet || 'No diet information documented'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </HistorySection>
  );

  const renderSurgicalHistory = () => (
    <HistorySection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h3">
          Surgical History
        </Typography>
        {editMode && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={handleOpenSurgicalHistoryDialog}
          >
            Add Surgery
          </Button>
        )}
      </Box>
      
      {surgicalHistory.length === 0 ? (
        <Alert severity="info">No surgical history documented</Alert>
      ) : (
        surgicalHistory.map((surgery) => (
          <HistoryItem key={surgery.id}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {surgery.procedure}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date: {new Date(surgery.date).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Facility: {surgery.facility}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Surgeon: {surgery.surgeon}
                </Typography>
                {surgery.notes && (
                  <Typography variant="body2">{surgery.notes}</Typography>
                )}
              </Box>
              {editMode && (
                <Box>
                  <IconButton size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </HistoryItem>
        ))
      )}
    </HistorySection>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => {
            setError(null);
            loadMedicalHistory();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="medical history tabs">
          <Tab 
            icon={<WarningIcon />} 
            label="Allergies & Problems" 
            iconPosition="start"
          />
          <Tab 
            icon={<VaccinesIcon />} 
            label="Immunizations" 
            iconPosition="start"
          />
          <Tab 
            icon={<FamilyRestroomIcon />} 
            label="Family History" 
            iconPosition="start"
          />
          <Tab 
            icon={<SocialDistanceIcon />} 
            label="Social History" 
            iconPosition="start"
          />
          <Tab 
            icon={<BuildIcon />} 
            label="Surgical History" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WarningIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="h3">
              Allergies
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => toggleSection('allergies')}
              sx={{ ml: 'auto' }}
            >
              {expandedSections.has('allergies') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.has('allergies')}>
            <AllergiesTab patient={patient} />
          </Collapse>
          
          <Divider sx={{ my: 3 }} />
          
          {renderProblemList()}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <VaccinesIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="h3">
              Immunizations
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => toggleSection('immunizations')}
              sx={{ ml: 'auto' }}
            >
              {expandedSections.has('immunizations') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.has('immunizations')}>
            <ImmunizationsSection patientId={patient?.id} />
          </Collapse>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {renderFamilyHistory()}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {renderSocialHistory()}
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        {renderSurgicalHistory()}
      </TabPanel>

      {/* Add Problem Dialog */}
      <Dialog open={problemDialogOpen} onClose={() => setProblemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Medical Problem</DialogTitle>
        <DialogContent>
                     <TextField
             autoFocus
             margin="dense"
             label="Condition/Problem"
             fullWidth
             variant="outlined"
             value={problemForm.condition}
             onChange={(e) => handleProblemFormChange('condition', e.target.value)}
             required
           />
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={problemForm.status}
              onChange={(e) => handleProblemFormChange('status', e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="chronic">Chronic</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            label="Onset Date"
            value={problemForm.onsetDate ? new Date(problemForm.onsetDate) : null}
            onChange={(date) => handleProblemFormChange('onsetDate', date ? date.toISOString().split('T')[0] : '')}
            slotProps={{
              textField: { margin: 'dense', fullWidth: true, variant: 'outlined', required: true }
            }}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Severity</InputLabel>
            <Select
              label="Severity"
              value={problemForm.severity}
              onChange={(e) => handleProblemFormChange('severity', e.target.value)}
            >
              <MenuItem value="mild">Mild</MenuItem>
              <MenuItem value="moderate">Moderate</MenuItem>
              <MenuItem value="severe">Severe</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Notes"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={problemForm.notes}
            onChange={(e) => handleProblemFormChange('notes', e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProblemDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddProblem} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Add Problem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Family History Dialog */}
      <Dialog open={familyHistoryDialogOpen} onClose={() => setFamilyHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Family Member</DialogTitle>
        <DialogContent>
                     <TextField
             autoFocus
             margin="dense"
             label="Relationship"
             fullWidth
             variant="outlined"
             value={familyHistoryForm.relationship}
             onChange={(e) => handleFamilyHistoryFormChange('relationship', e.target.value)}
             required
           />
                     <TextField
             margin="dense"
             label="Condition"
             fullWidth
             variant="outlined"
             value={familyHistoryForm.condition}
             onChange={(e) => handleFamilyHistoryFormChange('condition', e.target.value)}
             required
           />
                     <TextField
             margin="dense"
             label="Age"
             type="number"
             fullWidth
             variant="outlined"
             value={familyHistoryForm.age}
             onChange={(e) => handleFamilyHistoryFormChange('age', e.target.value)}
             required
           />
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={familyHistoryForm.status}
              onChange={(e) => handleFamilyHistoryFormChange('status', e.target.value)}
            >
              <MenuItem value="alive">Alive</MenuItem>
              <MenuItem value="deceased">Deceased</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Notes"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={familyHistoryForm.notes}
            onChange={(e) => handleFamilyHistoryFormChange('notes', e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFamilyHistoryDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddFamilyHistory} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Add Family Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Social History Dialog */}
      <Dialog open={socialHistoryDialogOpen} onClose={() => setSocialHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Social History</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
                         <Grid item xs={12} md={6}>
               <FormControl fullWidth margin="dense">
                 <InputLabel>Smoking Status</InputLabel>
                 <Select
                   label="Smoking Status"
                   value={socialHistoryForm.smoking.status}
                   onChange={(e) => handleSocialHistoryFormChange('smoking', { status: e.target.value, details: socialHistoryForm.smoking.details })}
                 >
                   <MenuItem value="unknown">Unknown</MenuItem>
                   <MenuItem value="never">Never</MenuItem>
                   <MenuItem value="former">Former</MenuItem>
                   <MenuItem value="current">Current</MenuItem>
                 </Select>
               </FormControl>
               <TextField
                 margin="dense"
                 label="Smoking Details"
                 fullWidth
                 variant="outlined"
                 value={socialHistoryForm.smoking.details}
                 onChange={(e) => handleSocialHistoryFormChange('smoking', { status: socialHistoryForm.smoking.status, details: e.target.value })}
                 placeholder="e.g., 1 pack/day for 10 years, quit 2 years ago"
               />
             </Grid>
             <Grid item xs={12} md={6}>
               <FormControl fullWidth margin="dense">
                 <InputLabel>Alcohol Status</InputLabel>
                 <Select
                   label="Alcohol Status"
                   value={socialHistoryForm.alcohol.status}
                   onChange={(e) => handleSocialHistoryFormChange('alcohol', { status: e.target.value, details: socialHistoryForm.alcohol.details })}
                 >
                   <MenuItem value="unknown">Unknown</MenuItem>
                   <MenuItem value="never">Never</MenuItem>
                   <MenuItem value="former">Former</MenuItem>
                   <MenuItem value="current">Current</MenuItem>
                 </Select>
               </FormControl>
               <TextField
                 margin="dense"
                 label="Alcohol Details"
                 fullWidth
                 variant="outlined"
                 value={socialHistoryForm.alcohol.details}
                 onChange={(e) => handleSocialHistoryFormChange('alcohol', { status: socialHistoryForm.alcohol.status, details: e.target.value })}
                 placeholder="e.g., 2 drinks/week, social drinking"
               />
             </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Occupation"
                fullWidth
                variant="outlined"
                value={socialHistoryForm.occupation}
                onChange={(e) => handleSocialHistoryFormChange('occupation', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Living Situation"
                fullWidth
                variant="outlined"
                value={socialHistoryForm.livingSituation}
                onChange={(e) => handleSocialHistoryFormChange('livingSituation', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Exercise"
                fullWidth
                variant="outlined"
                value={socialHistoryForm.exercise}
                onChange={(e) => handleSocialHistoryFormChange('exercise', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Diet"
                fullWidth
                variant="outlined"
                value={socialHistoryForm.diet}
                onChange={(e) => handleSocialHistoryFormChange('diet', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSocialHistoryDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateSocialHistory} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Surgical History Dialog */}
      <Dialog open={surgicalHistoryDialogOpen} onClose={() => setSurgicalHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Surgical Procedure</DialogTitle>
        <DialogContent>
                     <TextField
             autoFocus
             margin="dense"
             label="Procedure"
             fullWidth
             variant="outlined"
             value={surgicalHistoryForm.procedure}
             onChange={(e) => handleSurgicalHistoryFormChange('procedure', e.target.value)}
             required
           />
          <DatePicker
            label="Date"
            value={surgicalHistoryForm.date ? new Date(surgicalHistoryForm.date) : null}
            onChange={(date) => handleSurgicalHistoryFormChange('date', date ? date.toISOString().split('T')[0] : '')}
            slotProps={{
              textField: { margin: 'dense', fullWidth: true, variant: 'outlined', required: true }
            }}
          />
                     <TextField
             margin="dense"
             label="Facility"
             fullWidth
             variant="outlined"
             value={surgicalHistoryForm.facility}
             onChange={(e) => handleSurgicalHistoryFormChange('facility', e.target.value)}
             required
           />
          <TextField
            margin="dense"
            label="Surgeon"
            fullWidth
            variant="outlined"
            value={surgicalHistoryForm.surgeon}
            onChange={(e) => handleSurgicalHistoryFormChange('surgeon', e.target.value)}
          />
          <TextField
            margin="dense"
            label="Notes"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={surgicalHistoryForm.notes}
            onChange={(e) => handleSurgicalHistoryFormChange('notes', e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSurgicalHistoryDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSurgicalHistory} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Add Surgery'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MedicalHistoryTab;
